// @ts-strict-ignore
import { IsThreeJsType } from '@3d-scene/common/util/is-threejs-type';
import { TextureUtils } from '../model3d/util/texture-utils';
import { Box3, BufferAttribute, BufferGeometry, Color, ExtrudeGeometry, Float32BufferAttribute, LineSegments, Material, Matrix3, Mesh, Object3D, Texture, Vector2, Vector3 } from 'three';
import { Dictionary } from 'typescript-collections';
import { ObjInfo, ObjTextureInfo } from '@article/model/obj-info';
import { ArticleContainer } from '../model3d/article-container';
import { StringUtils } from '@shared/util/string-utils';
import { IArticleTextureParameters } from '@project/components/import-export/generator/i-article-texture-parameters';
import { ArticleParametersCreator } from '@project/components/import-export/generator/article-parameters-creator';
import { NumberUtils } from '@shared/util/number-utils';
import { Md5 } from 'ts-md5/dist/md5';
import { MathUtils } from '../common/util/math-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { SpriteMaterial } from '../material/sprite-material';
import { Object3DUtils } from '../common/util/object-3d-utils';
import { ProjectUserModel } from '@project/model/project-user.model';
import { MeshUtils } from '../common/util/mesh-utils';
import { TextureElementVO } from '@article/model/vo/texture-element.vo';
import { LabeledFlag } from '../model3d/labeled-flag';
import { Face3, GeometryUtils } from '../common/util/geometry-utils';
import { ContainerUtils } from '../model3d/container.utils';
import { CompositionContainer } from '../model3d/composition-container';

export class ObjExporter {
  public static readonly BOUNDING_BOX_NAME: string = 'boundingbox';
  public static readonly PNG_FORMAT: string = '.png';
  public static readonly CONNECTIONS_PREFIXES: string[] = ['cn', 'ta', 'mp', 'ww', 'fw', 'la', 'ra'];
  public static readonly NAME_PREFIXES_TO_EXCLUDE_FROM_NUMERATION: string[] = ObjExporter.CONNECTIONS_PREFIXES.concat([
    'wirecontour',
    'blockingZone',
    TextureElementVO.TEXTURE_ELEMENT_PREFIX
  ]);
  private static readonly PARSING_QUEUE_DELAY: number = 15;

  public static readonly SPECULAR_SUFFIX: string = '_specular';
  public static readonly NORMAL_SUFFIX: string = '_normal';

  // Blender object names are restricted to 63 bytes
  private static readonly MAX_NAME_LENGTH: number = 63;
  private static readonly NAME_DELIMETER: string = '_';

  private static readonly DECIMAL_COUNT_V_MESH: number = 10;
  private static readonly DECIMAL_COUNT_VN_MESH: number = 15;

  public static readonly DECIMAL_COUNT_V_LINE_SEGMENT: number = 10;
  private static readonly DECIMAL_COUNT_V_BOUNDING_BOX: number = 10;

  private static readonly DECIMAL_COUNT_OBJECT_POSITION: number = 4;

  public articleParametersCreator: ArticleParametersCreator;

  private objInfo: ObjInfo = new ObjInfo();
  private gcount: number = 0;
  private indV: number = 0;
  private indVn: number = 0;
  private indVt: number = 0;
  private indF: number = 0;
  private indL: number = 0;
  private _useShortNames: boolean;

  private _materialsCollection: Dictionary<string, boolean> = new Dictionary<string, boolean>();
  private _articleParametersToMaterialId: Dictionary<string, string> = new Dictionary<string, string>();
  private _materialTextureIdCounter: number = 0;

  private _processingArticleContainer: ArticleContainer = null;

  private _resultHandler: Function = null;

  private _parsingQueue: Function[] = [];

  constructor(projectUserModel: ProjectUserModel) {
    this.articleParametersCreator = new ArticleParametersCreator(projectUserModel);
  }

  private checkUV(value: number, checkBounds: boolean): number {
    if (!checkBounds) {
      return value;
    }
    if (value > 1) {
      return 1;
    }
    if (value < 0) {
      return 0;
    }
    return value;
  }

  private createMeshName(mesh: Mesh): string {
    let name: string = this.getNextName(mesh.name);

    // If the export is created for processing in blender (pdf-report, rail-front-view),
    // we add the ArticleId to the name of some meshes.
    // (Note: The flag looks inverted: if it is true we make the name longer, not shorter)
    if (this._useShortNames) {
      const parent: Object3D = mesh.parent;

      // if parent is an ArticleContainer
      const container: Object3D = parent?.parent;
      if (ContainerUtils.isArticleContainer(container)) {
        name = this.appendArticleId(name, container);
      }

      // If parent is a LabeledFlag
      if (parent instanceof LabeledFlag) {
        const container = parent.container;
        if (ContainerUtils.isArticleContainer(container)) {
          name = this.appendArticleId(name, container);
        }
      }
    }

    return name;
  }

  private writeMesh(mesh: Mesh, exportMaterials: boolean, exportArticleTextures: boolean, exportInverseTextures: boolean): void {
    const aVl: Dictionary<string, number> = new Dictionary<string, number>();
    const aVnl: Dictionary<string, number> = new Dictionary<string, number>();
    const aVtl: Dictionary<string, number> = new Dictionary<string, number>();

    const aV: string[] = [];
    const aVn: string[] = [];
    const aVt: string[] = [];
    const aF: string[] = [];

    this.objInfo.objContent += '\n';
    const name: string = this.createMeshName(mesh);
    this.objInfo.objContent += 'g ' + name + '\n';
    this.gcount++;
    if (exportMaterials) {
      this.writeMtl(mesh.material as Material, exportArticleTextures);
    }
    this.objInfo.objContent += 'o ' + name + '\n';
    const checkUVBounds: boolean = !MeshUtils.isTextureElement(name);
    let face: Face3 = null;
    let uv: Vector2[] = null;
    let n0: Vector3, n1: Vector3, n2: Vector3;

    const geometry: BufferGeometry = mesh.geometry.clone();
    if (geometry instanceof ExtrudeGeometry) {
      const uvAttribute = geometry.attributes.uv as BufferAttribute;
      if (uvAttribute) {
        geometry.setAttribute('uv', new Float32BufferAttribute(Array.from(uvAttribute.array).map(() => 1), 2));
      }
    }

    geometry.computeVertexNormals();
    const normals = geometry.attributes.normals;
    if (normals) {
      normals.needsUpdate = true;
    }

    let vta: number, vtb: number, vtc: number;

    let na: number, nb: number, nc: number;

    let tmp: Vector3 = new Vector3();
    const aRef: number[] = [0, 0, 0];

    const faces: Face3[] = GeometryUtils.getFaces(geometry);
    const vertices: Vector3[] = GeometryUtils.getVertices(geometry);
    const uvArray: Vector2[][] = GeometryUtils.getFaceVertexUvs(geometry);

    let originalFace: Face3;

    for (let i = 0; i < faces.length; i++) {
      originalFace = faces[i];
      // check faces. If face has two equal points, then it's removed.
      if (
        vertices[originalFace.a].equals(vertices[originalFace.b]) ||
        vertices[originalFace.a].equals(vertices[originalFace.c]) ||
        vertices[originalFace.b].equals(vertices[originalFace.c])
      ) {
        faces.splice(i, 1);
        uvArray.splice(i, 1);
      } else {
        // check if normal is valid, so basically this checks if vertices of face are on a single line
        const checkNormal: Vector3 = originalFace.normal;
        if (isNaN(checkNormal.x) || isNaN(checkNormal.y) || isNaN(checkNormal.z)) {
          faces.splice(i, 1);
          uvArray.splice(i, 1);
        }
      }
    }

    let scaleU: number = 1;
    let scaleV: number = 1;

    if ((mesh.material as any).hasOwnProperty('map') && IsThreeJsType.texture((mesh.material as any)['map'])) {
      scaleU = (mesh.material as any)['map'].wrapS / (mesh.material as any)['map'].wrapT;
      scaleV = (mesh.material as any)['map'].wrapT / (mesh.material as any)['map'].wrapS;
    }

    for (let i = 0; i < faces.length; i++) {
      // invert faces
      originalFace = faces[i];
      face = originalFace.clone();
      uv = uvArray[i];

      for (let j = 0; j < 3; j++) {
        if (j === 0) {
          tmp = new Vector3().copy(vertices[face.a]);
        } else if (j === 1) {
          tmp = new Vector3().copy(vertices[face.b]);
        } else if (j === 2) {
          tmp = new Vector3().copy(vertices[face.c]);
        }

        tmp.applyMatrix4(mesh.matrixWorld);

        aRef[j] = this.checkDoubles(
          aV,
          aVl,
          'v ' +
            tmp.x.toFixed(ObjExporter.DECIMAL_COUNT_V_MESH) +
            ' ' +
            tmp.y.toFixed(ObjExporter.DECIMAL_COUNT_V_MESH) +
            ' ' +
            tmp.z.toFixed(ObjExporter.DECIMAL_COUNT_V_MESH) +
            '\n'
        );
      }

      if (uv) {
        if (exportInverseTextures) {
          vta = this.checkDoubles(aVt, aVtl, ('vt ' + this.checkUV((1 - uv[0].x) * scaleU, checkUVBounds) + ' ' + this.checkUV(uv[0].y * scaleV, checkUVBounds) + '\n'));
          vtb = this.checkDoubles(aVt, aVtl, ('vt ' + this.checkUV((1 - uv[1].x) * scaleU, checkUVBounds) + ' ' + this.checkUV(uv[1].y * scaleV, checkUVBounds) + '\n'));
          vtc = this.checkDoubles(aVt, aVtl, ('vt ' + this.checkUV((1 - uv[2].x) * scaleU, checkUVBounds) + ' ' + this.checkUV(uv[2].y * scaleV, checkUVBounds) + '\n'));
        } else {
          vta = this.checkDoubles(aVt, aVtl, ('vt ' + this.checkUV((uv[0].x) * scaleU, checkUVBounds) + ' ' + this.checkUV(uv[0].y * scaleV, checkUVBounds) + '\n'));
          vtb = this.checkDoubles(aVt, aVtl, ('vt ' + this.checkUV((uv[1].x) * scaleU, checkUVBounds) + ' ' + this.checkUV(uv[1].y * scaleV, checkUVBounds) + '\n'));
          vtc = this.checkDoubles(aVt, aVtl, ('vt ' + this.checkUV((uv[2].x) * scaleU, checkUVBounds) + ' ' + this.checkUV(uv[2].y * scaleV, checkUVBounds) + '\n'));
        }
      }

      if (face.vertexNormals.length === 3) {
        n0 = new Vector3().copy(face.vertexNormals[0]);
        n1 = new Vector3().copy(face.vertexNormals[1]);
        n2 = new Vector3().copy(face.vertexNormals[2]);
      } else {
        n0 = new Vector3().copy(face.normal);
        n1 = new Vector3().copy(face.normal);
        n2 = new Vector3().copy(face.normal);
      }

      const normalMatrixWorld: Matrix3 = new Matrix3().getNormalMatrix(mesh.matrixWorld);
      n0.applyMatrix3(normalMatrixWorld);
      n1.applyMatrix3(normalMatrixWorld);
      n2.applyMatrix3(normalMatrixWorld);

      // exclude faces with nullable normal vectors
      if (n0.length() === 0 || n1.length() === 0 || n2.length() === 0) {
        continue;
      }

      na = this.checkDoubles(
        aVn,
        aVnl,
        'vn ' +
          n0.x.toFixed(ObjExporter.DECIMAL_COUNT_VN_MESH) +
          ' ' +
          n0.y.toFixed(ObjExporter.DECIMAL_COUNT_VN_MESH) +
          ' ' +
          n0.z.toFixed(ObjExporter.DECIMAL_COUNT_VN_MESH) +
          '\n'
      );
      nb = this.checkDoubles(
        aVn,
        aVnl,
        'vn ' +
          n1.x.toFixed(ObjExporter.DECIMAL_COUNT_VN_MESH) +
          ' ' +
          n1.y.toFixed(ObjExporter.DECIMAL_COUNT_VN_MESH) +
          ' ' +
          n1.z.toFixed(ObjExporter.DECIMAL_COUNT_VN_MESH) +
          '\n'
      );
      nc = this.checkDoubles(
        aVn,
        aVnl,
        'vn ' +
          n2.x.toFixed(ObjExporter.DECIMAL_COUNT_VN_MESH) +
          ' ' +
          n2.y.toFixed(ObjExporter.DECIMAL_COUNT_VN_MESH) +
          ' ' +
          n2.z.toFixed(ObjExporter.DECIMAL_COUNT_VN_MESH) +
          '\n'
      );

      aF.push(
        'f ' +
          (aRef[0] + this.indV) +
          '/' +
          this.getSumAsString(vta, this.indVt) +
          '/' +
          (na + this.indVn) +
          ' ' +
          (aRef[1] + this.indV) +
          '/' +
          this.getSumAsString(vtb, this.indVt) +
          '/' +
          (nb + this.indVn) +
          ' ' +
          (aRef[2] + this.indV) +
          '/' +
          this.getSumAsString(vtc, this.indVt) +
          '/' +
          (nc + this.indVn) +
          '\n'
      );
    }

    this.indV += aV.length;
    this.indVn += aVn.length;
    this.indVt += aVt.length;
    this.indF += aF.length;

    this.objInfo.objContent += '# Number of Vertices: ' + aV.length + '\n';
    for (let i = 0; i < aV.length; ++i) {
      this.objInfo.objContent += aV[i];
    }

    this.objInfo.objContent += '\n# Number of Normals: ' + aVn.length + '\n';
    for (let i = 0; i < aVn.length; ++i) {
      this.objInfo.objContent += aVn[i];
    }

    this.objInfo.objContent += '\n# Number of Texture Vertices: ' + aVt.length + '\n';
    for (let i = 0; i < aVt.length; ++i) {
      this.objInfo.objContent += aVt[i];
    }

    this.objInfo.objContent += '\n# Number of Polygons: ' + aF.length + '\n';
    for (let i = 0; i < aF.length; ++i) {
      this.objInfo.objContent += aF[i];
    }
  }

  private getSumAsString(vt: number, intVt: number): string {
    const result: number = vt + intVt;
    return NumberUtils.isNumber(result) ? result.toString() : '';
  }

  private createSegmentSetName(segmentSet: LineSegments): string {
    let name: string = this.getNextName(segmentSet.name);
    const container: Object3D = segmentSet.parent;
    if (this._useShortNames && ContainerUtils.isArticleContainer(container)) {
      name = this.appendArticleId(name, container);
    }
    return name;
  }

  private writeLineSegments(lineSegments: LineSegments, exportMaterials: boolean): void {
    const aVl: Dictionary<string, number> = new Dictionary<string, number>();
    const aV: string[] = [];
    const aL: string[] = [];

    this.objInfo.objContent += '\n';
    if (lineSegments.name === 'MeshOutline') {
      return;
    }
    let name: string = this.createSegmentSetName(lineSegments);
    this.objInfo.objContent += 'g ' + name + '\n';
    this.gcount++;
    if (exportMaterials) {
      this.writeMtl(lineSegments.material as Material, true);
    }
    this.objInfo.objContent += 'o ' + name + '\n';

    let tmp: Vector3 = new Vector3();
    const aRef: number[] = [0, 0];

    const vertices = GeometryUtils.getVertices(lineSegments.geometry);

    for (let i = 0; i < vertices.length; i += 2) {
      for (let j = 0; j < 2; j++) {
        if (j === 0) {
          tmp = vertices[i];
        } else if (j === 1) {
          tmp = vertices[i + 1];
        }
        tmp.applyMatrix4(lineSegments.matrixWorld);
        aRef[j] = this.checkDoubles(
          aV,
          aVl,
          'v ' +
            tmp.x.toFixed(ObjExporter.DECIMAL_COUNT_V_LINE_SEGMENT) +
            ' ' +
            tmp.y.toFixed(ObjExporter.DECIMAL_COUNT_V_LINE_SEGMENT) +
            ' ' +
            tmp.z.toFixed(ObjExporter.DECIMAL_COUNT_V_LINE_SEGMENT) +
            '\n'
        );
      }
      aL.push('l ' + (aRef[0] + this.indV) + ' ' + (aRef[1] + this.indV) + '\n');
    }

    this.indV += aV.length;
    this.indL += aL.length;

    this.objInfo.objContent += '# Number of vertices: ' + aV.length + '\n';
    for (let i = 0; i < aV.length; ++i) {
      this.objInfo.objContent += aV[i];
    }

    this.objInfo.objContent += '\n# Number of Lines: ' + aL.length + '\n';
    for (let i = 0; i < aL.length; ++i) {
      this.objInfo.objContent += aL[i];
    }
  }

  private writeCompositionContainer(object3d: CompositionContainer) {
    this.objInfo.objContent += '#composition ' + object3d.compositionGuid;
    if (object3d.position) {
      this.objInfo.objContent +=
        ' ' +
        object3d.position.x.toFixed(ObjExporter.DECIMAL_COUNT_OBJECT_POSITION) +
        ' ' +
        object3d.position.y.toFixed(ObjExporter.DECIMAL_COUNT_OBJECT_POSITION) +
        ' ' +
        object3d.position.z.toFixed(ObjExporter.DECIMAL_COUNT_OBJECT_POSITION);
    }
    this.objInfo.objContent += '\n';
  }

  private writeArticleContainer(object3d: ArticleContainer) {
    this.objInfo.objContent += '#articleUsage ' + object3d.articleGUID;
    if (object3d.position) {
      this.objInfo.objContent +=
        ' ' +
        object3d.position.x.toFixed(ObjExporter.DECIMAL_COUNT_OBJECT_POSITION) +
        ' ' +
        object3d.position.y.toFixed(ObjExporter.DECIMAL_COUNT_OBJECT_POSITION) +
        ' ' +
        object3d.position.z.toFixed(ObjExporter.DECIMAL_COUNT_OBJECT_POSITION);
      this.objInfo.objContent +=
        ' ' +
        MathUtils.toDegrees(object3d.rotation.x).toFixed(ObjExporter.DECIMAL_COUNT_OBJECT_POSITION) +
        ' ' +
        MathUtils.toDegrees(object3d.rotation.y).toFixed(ObjExporter.DECIMAL_COUNT_OBJECT_POSITION) +
        ' ' +
        MathUtils.toDegrees(object3d.rotation.z).toFixed(ObjExporter.DECIMAL_COUNT_OBJECT_POSITION);
    }
    this.objInfo.objContent += '\n';
  }

  private writeBoundingBox(articleContainer: ArticleContainer): void {
    if (!articleContainer) {
      return;
    }
    const bounds: Box3 = articleContainer.bounds;
    if (!bounds) {
      return;
    }
    const aVl: Dictionary<string, number> = new Dictionary<string, number>();

    const aV: string[] = [];
    const aL: string[] = [];

    this.objInfo.objContent += '\n';
    const name: string = ObjExporter.BOUNDING_BOX_NAME + '_' + articleContainer.articleGUID;
    this.objInfo.objContent += 'g ' + name + '\n';
    this.gcount++;
    this.objInfo.objContent += 'o ' + name + '\n';

    let tmp: Vector3 = new Vector3();
    const aRef: number[] = [0, 0];

    // the lines, which are exported here, dont form a wholsome box, which could be nicely displayed to the user
    // he lines are just created here, so that every aabbPoints is included (just) once,
    // which is sufficient data for recreating the bounding box (on server side in ProjectGeometricDataInformation)
    const aabbPoints: Vector3[] = Object3DUtils.extractBoundingPoints(articleContainer.sceneTransformBounds);
    for (let i = 0; i < aabbPoints.length; i += 2) {
      for (let j = 0; j < 2; j++) {
        if (j === 0) {
          tmp = aabbPoints[i];
        } else if (j === 1) {
          tmp = aabbPoints[i + 1];
        }
        aRef[j] = this.checkDoubles(
          aV,
          aVl,
          'v ' +
            tmp.x.toFixed(ObjExporter.DECIMAL_COUNT_V_BOUNDING_BOX) +
            ' ' +
            tmp.y.toFixed(ObjExporter.DECIMAL_COUNT_V_BOUNDING_BOX) +
            ' ' +
            tmp.z.toFixed(ObjExporter.DECIMAL_COUNT_V_BOUNDING_BOX) +
            '\n'
        );
      }
      aL.push('l ' + (aRef[0] + this.indV) + ' ' + (aRef[1] + this.indV) + '\n');
    }

    this.indV += aV.length;
    this.indL += aL.length;

    this.objInfo.objContent += '# Number of vertices: ' + aV.length + '\n';
    for (let i = 0; i < aV.length; ++i) {
      this.objInfo.objContent += aV[i];
    }

    this.objInfo.objContent += '\n# Number of Lines: ' + aL.length + '\n';
    for (let i = 0; i < aL.length; ++i) {
      this.objInfo.objContent += aL[i];
    }
  }

  private getNextName(object3dname: string): string {
    let name: string = object3dname;
    if (StringUtils.isEmptyOrLiteralNull(name)) {
      name = 'g' + this.gcount;
    } else {
      let shouldBeExcluded: Boolean = false;
      for (const prefix of ObjExporter.NAME_PREFIXES_TO_EXCLUDE_FROM_NUMERATION) {
        if (name.indexOf(prefix) === 0) {
          shouldBeExcluded = true;
          break;
        }
      }

      if (!shouldBeExcluded) {
        name = name + this.gcount;
      }
    }
    return name;
  }

  private appendArticleId(name: string, articleContainer: ArticleContainer): string {
    const articleGUID: string = articleContainer.articleGUID;
    if (name.length + articleGUID.length + ObjExporter.NAME_DELIMETER.length > ObjExporter.MAX_NAME_LENGTH) {
      name = name.substr(0, ObjExporter.MAX_NAME_LENGTH - articleGUID.length - ObjExporter.NAME_DELIMETER.length);
    }
    return name + ObjExporter.NAME_DELIMETER + articleGUID;
  }

  private checkDoubles(arr: string[], arrl: Dictionary<string, number>, string: string): number {
    const index = arrl.getValue(string);
    if (index == null || index === undefined) {
      arr.push(string);
      arrl.setValue(string, arr.length);
      return arr.length;
    } else {
      return index;
    }
  }

  protected parse(
    object3d: Object3D,
    exportMeshes: boolean,
    exportMaterials: boolean,
    exportBoundingBoxes: boolean,
    exportProjectMetaInformation: boolean,
    exportArticleTextures: boolean,
    exportInverseTextures: boolean,
    isObjectContainer3DIncludedInExportFunction: Function
  ): boolean {
    if (isObjectContainer3DIncludedInExportFunction && !isObjectContainer3DIncludedInExportFunction(object3d)) {
      return true;
    }
    let exportChildren: boolean = true;
    if (ContainerUtils.isArticleContainer(object3d)) {
      exportChildren = object3d.isExportChildren();
    }
    this.parseObject(
      object3d,
      exportMeshes,
      exportMaterials,
      exportBoundingBoxes,
      exportProjectMetaInformation,
      exportArticleTextures,
      exportInverseTextures
    );
    if (exportChildren && ArrayUtils.isNotEmpty(object3d.children)) {
      for (let i = 0; i < object3d.children.length; i++) {
        const parseFinished: boolean = this.parse(
          object3d.children[i],
          exportMeshes,
          exportMaterials,
          exportBoundingBoxes,
          exportProjectMetaInformation,
          exportArticleTextures,
          exportInverseTextures,
          isObjectContainer3DIncludedInExportFunction
        );
        if (!parseFinished) {
          return false;
        }
      }
    }
    return true;
  }

  protected scheduleParse(
    object3d: Object3D,
    exportMeshes: boolean,
    exportMaterials: boolean,
    exportBoundingBoxes: boolean,
    exportProjectMetaInformation: boolean,
    exportArticleTextures: boolean,
    exportInverseTextures: boolean,
    isObjectContainer3DIncludedInExportFunction: Function,
    onFinish: Function
  ): void {
    this.createParseFunctions(
      object3d,
      exportMeshes,
      exportMaterials,
      exportBoundingBoxes,
      exportProjectMetaInformation,
      exportArticleTextures,
      exportInverseTextures,
      isObjectContainer3DIncludedInExportFunction
    );
    if (ArrayUtils.isEmpty(this._parsingQueue)) {
      if (onFinish) {
        onFinish();
      }
    } else {
      this.processParsingQueue(onFinish, 0);
    }
  }

  private parseObject(
    object3d: Object3D,
    exportMeshes: boolean,
    exportMaterials: boolean,
    exportBoundingBoxes: boolean,
    exportProjectMetaInformation: boolean,
    exportArticleTextures: boolean,
    exportInverseTextures: boolean
  ) {
    if (IsThreeJsType.mesh(object3d)) {
      if (exportMeshes) {
        this.writeMesh(object3d, exportMaterials, exportArticleTextures, exportInverseTextures);
      }
      return;
    }
    if (IsThreeJsType.lineSegments(object3d)) {
      this.writeLineSegments(object3d, exportMaterials);
      return;
    }
    if (ContainerUtils.isCompositionContainer(object3d)) {
      if (exportProjectMetaInformation) {
        this.writeCompositionContainer(object3d);
      }
      return;
    }
    if (ContainerUtils.isArticleContainer(object3d)) {
      this._processingArticleContainer = object3d;
      if (exportProjectMetaInformation) {
        this.writeArticleContainer(object3d);
      }
      if (exportBoundingBoxes) {
        this.writeBoundingBox(object3d);
      }
    }
  }

  private createParseFunctions(
    object3d: Object3D,
    exportMeshes: boolean,
    exportMaterials: boolean,
    exportBoundingBoxes: boolean,
    exportProjectMetaInformation: boolean,
    exportArticleTextures: boolean,
    exportInverseTextures: boolean,
    isObjectContainer3DIncludedInExportFunction: Function
  ): void {
    if (isObjectContainer3DIncludedInExportFunction && !isObjectContainer3DIncludedInExportFunction(object3d)) {
      return;
    }
    let exportChildren: boolean = true;
    if (ContainerUtils.isArticleContainer(object3d)) {
      exportChildren = object3d.isExportChildren();
    }
    const parseFunction: Function = () =>
      this.parseObject(
        object3d,
        exportMeshes,
        exportMaterials,
        exportBoundingBoxes,
        exportProjectMetaInformation,
        exportArticleTextures,
        exportInverseTextures
      );
    this._parsingQueue.push(parseFunction);
    if (exportChildren) {
      for (let i = 0; i < object3d.children.length; i++) {
        this.createParseFunctions(
          object3d.children[i],
          exportMeshes,
          exportMaterials,
          exportBoundingBoxes,
          exportProjectMetaInformation,
          exportArticleTextures,
          exportInverseTextures,
          isObjectContainer3DIncludedInExportFunction
        );
      }
    }
  }

  private processParsingQueue(onFinish: Function, counter: number): void {
    const parsingFunction: Function = this._parsingQueue.shift();
    if (parsingFunction) {
      if (counter % 500 !== 0) {
        parsingFunction();
        this.processParsingQueue(onFinish, counter + 1);
      } else {
        setTimeout(() => {
          parsingFunction();
          this.processParsingQueue(onFinish, counter + 1);
        }, ObjExporter.PARSING_QUEUE_DELAY);
      }
    } else if (onFinish) {
      onFinish();
    }
  }

  private writeMtl(material: Material, exportArticleTextures: boolean): void {
    if (material instanceof SpriteMaterial) {
      if (material.imageBitmapData && material.map) {
        this.writeBitmapMaterial(material, 'texture' + this._materialTextureIdCounter);
        this._materialTextureIdCounter++;
      } else {
        this.writeMaterial(material);
      }
    } else if ((material as any).hasOwnProperty('map') && IsThreeJsType.texture((material as any)['map'])) {
      this.writeTexture(material, exportArticleTextures);
    } else {
      this.writeMaterial(material);
    }
  }

  private writeTexture(material: Material, exportArticleTextures: boolean): void {
    let articleTextureParameters: IArticleTextureParameters = null;
    if (!exportArticleTextures && this.articleParametersCreator) {
      articleTextureParameters = this.articleParametersCreator.createArticleParameters(this._processingArticleContainer, material);
    }
    if (articleTextureParameters) {
      const textureParameters: string = articleTextureParameters.getTextureParameters();
      let materialId: string = this._articleParametersToMaterialId.getValue(textureParameters);
      if (!materialId) {
        materialId = 'texture' + this._materialTextureIdCounter;
        this._materialTextureIdCounter++;
        this._articleParametersToMaterialId.setValue(textureParameters, materialId);
      }
      this.writeArticleTextureUrlMaterial(articleTextureParameters, material, materialId);
    } else {
      this.writeBitmapMaterial(material, 'texture' + this._materialTextureIdCounter);
      this._materialTextureIdCounter++;
    }
  }

  private writeBitmapMaterial(mat: Material, materialId: string): void {
    const matName: string = 'mb' + materialId;
    this.objInfo.objContent += 'usemtl ' + matName + '\n';
    if (!this._materialsCollection.getValue(matName)) {
      let bitmapTexture: Texture;
      let imgBase64: string = '';
      let texture: ObjTextureInfo;
      const materialObject = mat as any;
      if (materialObject.hasOwnProperty('map') && IsThreeJsType.texture(materialObject['map'])) {
        bitmapTexture = materialObject['map'];
        if (mat instanceof SpriteMaterial) {
          if (mat.map.image instanceof HTMLImageElement) {
            imgBase64 = mat.map.image.src;
          } else {
            imgBase64 = TextureUtils.getDataURLFromCanvas(mat.map.image);
          }
        } else {
          imgBase64 = bitmapTexture.image.src;
        }

        this._materialsCollection.setValue(matName, true);
        this.objInfo.mtlContent += 'newmtl ' + matName + '\n';
        this.objInfo.mtlContent += 'map_Kd ' + matName + ObjExporter.PNG_FORMAT + '\n';
        texture = new ObjTextureInfo();
        texture.name = matName + ObjExporter.PNG_FORMAT;
        texture.content = StringUtils.base64ToByteArray(imgBase64);
        this.objInfo.textures.push(texture);
      }
      if (materialObject.hasOwnProperty('specularMap') && IsThreeJsType.texture(materialObject['specularMap'])) {
        bitmapTexture = materialObject['specularMap'];
        imgBase64 = bitmapTexture.image.src;
        this.objInfo.mtlContent += 'map_Ks ' + matName + ObjExporter.SPECULAR_SUFFIX + ObjExporter.PNG_FORMAT + '\n';
        texture = new ObjTextureInfo();
        texture.name = matName + '_specular' + ObjExporter.PNG_FORMAT;
        texture.content = StringUtils.base64ToByteArray(imgBase64);

        this.objInfo.textures.push(texture);
      }
      if (materialObject.hasOwnProperty('normalMap') && IsThreeJsType.texture(materialObject['normalMap'])) {
        bitmapTexture = materialObject['normalMap'];
        imgBase64 = bitmapTexture.image.src;
        this.objInfo.mtlContent += 'map_bump ' + matName + ObjExporter.NORMAL_SUFFIX + ObjExporter.PNG_FORMAT + '\n';
        texture = new ObjTextureInfo();
        texture.name = matName + '_normal' + ObjExporter.PNG_FORMAT;
        texture.content = StringUtils.base64ToByteArray(imgBase64);

        this.objInfo.textures.push(texture);
      }
      this.objInfo.mtlContent += 'd ' + mat.opacity.toString() + '\n';
      this.objInfo.mtlContent += '\n';
    }
  }

  private writeArticleTextureUrlMaterial(articleParameters: IArticleTextureParameters, mat: Material, materialId: string): void {
    const matName: string = 'mb' + materialId;
    this.objInfo.objContent += 'usemtl ' + matName + '\n';
    if (!this._materialsCollection.getValue(matName)) {
      let textureFileExtension: string = articleParameters.getTextureExtension();
      let materialTextureFileName: string = matName;
      if (textureFileExtension) {
        materialTextureFileName += '.' + textureFileExtension;
      }
      this._materialsCollection.setValue(matName, true);
      this.objInfo.mtlContent += 'newmtl ' + matName + '\n';
      this.objInfo.mtlContent += 'map_Kd ' + materialTextureFileName + '\n';

      let texture: ObjTextureInfo = new ObjTextureInfo();
      texture.name = materialTextureFileName;
      texture.articleParameters = articleParameters.getTextureParameters();

      this.objInfo.textures.push(texture);

      const specularParameters: string = articleParameters.getSpecularMapParameters();
      if (specularParameters) {
        textureFileExtension = articleParameters.getSpecularExtension();
        materialTextureFileName = matName + ObjExporter.SPECULAR_SUFFIX;
        if (textureFileExtension) {
          materialTextureFileName += '.' + textureFileExtension;
        }
        this.objInfo.mtlContent += 'map_Ks ' + materialTextureFileName + '\n';
        texture = new ObjTextureInfo();
        texture.name = materialTextureFileName;
        texture.articleParameters = specularParameters;

        this.objInfo.textures.push(texture);
      }

      const normalParameters: string = articleParameters.getNormalMapParameters();
      if (normalParameters) {
        textureFileExtension = articleParameters.getNormalExtension();
        materialTextureFileName = matName + ObjExporter.NORMAL_SUFFIX;
        if (textureFileExtension) {
          materialTextureFileName += '.' + textureFileExtension;
        }
        this.objInfo.mtlContent += 'map_bump ' + materialTextureFileName + '\n';
        texture = new ObjTextureInfo();
        texture.name = materialTextureFileName;
        texture.articleParameters = normalParameters;

        this.objInfo.textures.push(texture);
      }
      this.objInfo.mtlContent += 'd ' + mat.opacity.toString() + '\n';
      this.objInfo.mtlContent += '\n';
    }
  }

  private getDiffuseRGBColor(diffuseColor: Color, useBlackInCaseOriginalColorInformationIsMissing: boolean): Color {
    if (!diffuseColor) {
      return new Color();
    }
    // eslint-disable-next-line no-bitwise
    let diffuseRed: number = useBlackInCaseOriginalColorInformationIsMissing ? 0 : ((diffuseColor.getHex() >> 16) & 0xff) / 0xff;
    // eslint-disable-next-line no-bitwise
    let diffuseGreen: number = useBlackInCaseOriginalColorInformationIsMissing ? 0 : ((diffuseColor.getHex() >> 8) & 0xff) / 0xff;
    // eslint-disable-next-line no-bitwise
    let diffuseBlue: number = useBlackInCaseOriginalColorInformationIsMissing ? 0 : (diffuseColor.getHex() & 0xff) / 0xff;
    if (!isNaN(diffuseColor.r)) {
      diffuseRed = diffuseColor.r;
    }
    if (!isNaN(diffuseColor.g)) {
      diffuseGreen = diffuseColor.g;
    }
    if (!isNaN(diffuseColor.b)) {
      diffuseBlue = diffuseColor.b;
    }
    return new Color(diffuseRed, diffuseGreen, diffuseBlue);
  }

  private getAmbientRGBColor(ambientColor: Color, useBlackInCaseOriginalColorInformationIsMissing: boolean): Color {
    if (!ambientColor) {
      return new Color();
    }
    // eslint-disable-next-line no-bitwise
    let ambientRed: number = useBlackInCaseOriginalColorInformationIsMissing ? 0 : ((ambientColor.getHex() >> 16) & 0xff) / 0xff;
    // eslint-disable-next-line no-bitwise
    let ambientGreen: number = useBlackInCaseOriginalColorInformationIsMissing ? 0 : ((ambientColor.getHex() >> 8) & 0xff) / 0xff;
    // eslint-disable-next-line no-bitwise
    let ambientBlue: number = useBlackInCaseOriginalColorInformationIsMissing ? 0 : (ambientColor.getHex() & 0xff) / 0xff;
    if (!isNaN(ambientColor.r)) {
      ambientRed = ambientColor.r;
    }
    if (!isNaN(ambientColor.g)) {
      ambientGreen = ambientColor.g;
    }
    if (!isNaN(ambientColor.b)) {
      ambientBlue = ambientColor.b;
    }
    return new Color(ambientRed, ambientGreen, ambientBlue);
  }

  private getSpecularRGBColor(specularColor: Color, useBlackInCaseOriginalColorInformationIsMissing: boolean): Color {
    if (!specularColor) {
      return new Color();
    }
    // eslint-disable-next-line no-bitwise
    let specularRed: number = useBlackInCaseOriginalColorInformationIsMissing ? 0 : ((specularColor.getHex() >> 16) & 0xff) / 0xff;
    // eslint-disable-next-line no-bitwise
    let specularGreen: number = useBlackInCaseOriginalColorInformationIsMissing ? 0 : ((specularColor.getHex() >> 8) & 0xff) / 0xff;
    // eslint-disable-next-line no-bitwise
    let specularBlue: number = useBlackInCaseOriginalColorInformationIsMissing ? 0 : (specularColor.getHex() & 0xff) / 0xff;
    if (!isNaN(specularColor.r)) {
      specularRed = specularColor.r;
    }
    if (!isNaN(specularColor.g)) {
      specularGreen = specularColor.g;
    }
    if (!isNaN(specularColor.b)) {
      specularBlue = specularColor.b;
    }
    return new Color(specularRed, specularGreen, specularBlue);
  }

  private getSpecular(shininess: number): number {
    if (NumberUtils.isNumber(shininess)) {
      return shininess;
    } else {
      // better would be to return some readjusted number here, which could be taken from mat.shininess
      return 20;
    }
  }

  private getMtlRGBColorString(color: Color): string {
    if (!color) {
      return '0 0 0';
    }
    return color.r.toFixed(4).toString() + ' ' + color.g.toFixed(4).toString() + ' ' + color.b.toFixed(4).toString();
  }

  private getMaterialIdForColorMaterial(mat: Material): string {
    if (!mat) {
      return '';
    }
    let materialId: string = '';
    const matObject: any = mat as any;

    if (IsThreeJsType.color(matObject['emissive'])) {
      const ambientRGBColor: Color = this.getAmbientRGBColor(matObject['emissive'], true);
      materialId += this.getMtlRGBColorString(ambientRGBColor);
    }

    if (IsThreeJsType.color(matObject['color'])) {
      const diffuseRGBColor: Color = this.getDiffuseRGBColor(matObject['color'], true);
      materialId += this.getMtlRGBColorString(diffuseRGBColor);
    }

    if (IsThreeJsType.color(matObject['specular'])) {
      const specularRGBColor: Color = this.getSpecularRGBColor(matObject['specular'], true);
      materialId += this.getMtlRGBColorString(specularRGBColor);
    }

    if (matObject.hasOwnProperty('shininess')) {
      materialId += this.getSpecular(matObject['shininess']);
    }

    // There might exist two color materials with equal colors but distinct alphas,
    // therefore alpha is considered here as well (aditionally to the rgb-color)
    const matAlpha: string = isNaN(mat.opacity) ? '' : mat.opacity.toString();
    materialId += matAlpha;

    return String(Md5.hashStr(materialId));
  }

  private writeMaterial(material: Material): void {
    const materialId: string = this.getMaterialIdForColorMaterial(material);
    const matName: string = 'm' + materialId;
    this.objInfo.objContent += 'usemtl ' + matName + '\n';
    if (!this._materialsCollection.getValue(matName)) {
      this._materialsCollection.setValue(matName, true);
      this.objInfo.mtlContent += 'newmtl ' + matName + '\n';

      const matObject: any = material as any;

      if (matObject.hasOwnProperty('shininess')) {
        this.objInfo.mtlContent += 'Ns ' + matObject['shininess'] + '\n';
      }

      if (IsThreeJsType.color(matObject['emissive'])) {
        this.objInfo.mtlContent += 'Ka ' + this.getMtlRGBColorString(matObject['emissive']) + '\n';
      }

      if (IsThreeJsType.color(matObject['color'])) {
        this.objInfo.mtlContent += 'Kd ' + this.getMtlRGBColorString(matObject['color']) + '\n';
      }

      if (IsThreeJsType.color(matObject['specular'])) {
        this.objInfo.mtlContent += 'Ks ' + this.getMtlRGBColorString(matObject['specular']) + '\n';
      }

      const matAlpha: string = material.opacity.toString();
      this.objInfo.mtlContent += 'd ' + matAlpha + '\n';

      this.objInfo.mtlContent += '\n';
    }
  }

  /**
   // Generates a string in the WaveFront obj format representing the object3D(s).
   //
   // @param object3d  Object3D. The Object3D to be exported to WaveFront obj format.
   // @param righthanded [optional] boolean. If the model output need to be flipped to righthanded system. Default = false (FLEX: true)
   // @param exportMeshes  [optional] boolean. If the meshes of the model should also be exported. Default = true.
   // @param exportMaterials [optional] boolean. If the materials of the model should also be exported. Default = true.
   // @param exportBoundingBoxes [optional] boolean. If the bounding boxes of the model should also be exported. Default = false.
   // @param exportProjectMetaInformation  [optional] boolean. If meta information for project articles and compositions should also
   //   be exported.Default = true.
   // @param exportArticleTextures [optional] boolean. If textures which belong to corresponding article should be encoded and exported.
   //   Default = true.
   // @param isObjectContainer3DIncludedInExportFunction	[optional] Function. Filter function, which takes an ObjectContainer3DObject
   //   and returns a Boolean, which determines if it should be included in export. Default = null.
   // @return ObjInfo object, representing current object3d
   */
  public export(
    object3d: Object3D,
    exportMeshes: boolean = true,
    exportMaterials: boolean = true,
    exportBoundingBoxes: boolean = false,
    exportProjectMetaInformation: boolean = true,
    exportArticleTextures: boolean = true,
    exportInverseTextures: boolean = true,
    useShortNames: boolean = false,
    isObjectContainer3DIncludedInExportFunction: Function = null
  ): ObjInfo {
    this._resultHandler = null;
    this.gcount = this.indV = this.indVn = this.indVt = this.indF = this.indL = 0;
    this._materialTextureIdCounter = 0;
    this._useShortNames = useShortNames;
    this.parse(
      object3d,
      exportMeshes,
      exportMaterials,
      exportBoundingBoxes,
      exportProjectMetaInformation,
      exportArticleTextures,
      exportInverseTextures,
      isObjectContainer3DIncludedInExportFunction
    );
    this.objInfo.objContent += '\n#\n# Total number of vertices: ' + this.indV;
    this.objInfo.objContent += '\n# Total number of Normals: ' + this.indVn;
    this.objInfo.objContent += '\n# Total number of Texture Vertices: ' + this.indVt;
    this.objInfo.objContent += '\n# Total number of Polygons: ' + this.indF;
    this.objInfo.objContent += '\n# Total number of Lines: ' + this.indL + '\n';
    this.objInfo.objContent += '\n# End of File';
    this.objInfo.verticesCount = this.indV;
    return this.objInfo;
  }

  /** @param resultHandler Function with single parameter of type ObjInfo. Is called after scheduled export completed. */
  public scheduleExport(
    resultHandler: Function,
    object3d: Object3D,
    exportMeshes: boolean = true,
    exportMaterials: boolean = true,
    exportBoundingBoxes: boolean = false,
    exportProjectMetaInformation: boolean = true,
    exportArticleTextures: boolean = true,
    exportInverseTextures: boolean = true,
    useShortNames: boolean = false,
    isObjectContainer3DIncludedInExportFunction: Function = null
  ): void {
    this._resultHandler = resultHandler;
    this.gcount = this.indV = this.indVn = this.indVt = this.indF = this.indL = 0;
    this._materialTextureIdCounter = 0;
    this._useShortNames = useShortNames;
    this.scheduleParse(
      object3d,
      exportMeshes,
      exportMaterials,
      exportBoundingBoxes,
      exportProjectMetaInformation,
      exportArticleTextures,
      exportInverseTextures,
      isObjectContainer3DIncludedInExportFunction,
      () => this.finishScheduledExport()
    );
  }

  private finishScheduledExport(): void {
    this.objInfo.objContent += '\n#\n# Total number of vertices: ' + this.indV;
    this.objInfo.objContent += '\n# Total number of Normals: ' + this.indVn;
    this.objInfo.objContent += '\n# Total number of Texture Vertices: ' + this.indVt;
    this.objInfo.objContent += '\n# Total number of Polygons: ' + this.indF;
    this.objInfo.objContent += '\n# Total number of Lines: ' + this.indL + '\n';
    this.objInfo.objContent += '\n# End of File';
    this.objInfo.verticesCount = this.indV;
    if (this._resultHandler) {
      this._resultHandler(this.objInfo);
    }
  }
}
